<template>
  <Form
    :id="'gform_' + gfid"
    :class="[
      'form dynamic-gravity-form',
      submitting ? 'hover:cursor-wait' : '',
    ]"
    @submit="onSubmit(values)"
    v-if="!confirmationMessage"
  >
    <component
      v-for="field in schema?.fields || []"
      :is="field.type === 'fieldset' ? 'fieldset' : 'div'"
      :key="field.name"
      :class="[
        field.type,
        field.type === 'fieldset'
          ? 'field-group-container grid gap-4 md:gap-y-0 grid-cols-1 md:grid-cols-2'
          : 'single-field-container',
      ]"
    >
      <div v-if="field.type === 'fieldset'" v-for="field in field.fields">
        <label
          :for="'field-' + input.id"
          :class="[field.css, field.inputType]"
          >{{ field.label }}</label
        >
        <Field
          :id="'field-' + input.id"
          :class="`text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait ${field.css}`"
          :key="field.name + '-' + input.id"
          :as="field.as"
          :name="field.name"
          :placeholder="field.label"
          :type="field.type"
          :rules="field.rules"
          :disabled="submitting"
          :autocomplete="field.autocomplete"
          v-model="values[field.name]"
        />
        <div
          v-if="validationErrors[field.id.split('.')[0]]"
          class="error-message"
        >
          {{ validationErrors[field.id.split(".")[0]] }}
        </div>
      </div>

      <div v-else-if="field.type === 'name'">
        <label :class="[field.css, field.inputType]">{{ field.label }}</label>
        <div class="flex-name">
          <div
            v-for="(input, index) in field.inputs"
            :key="`input-${index}`"
            :class="{ hidden: input.isHidden }"
          >
            <div v-show="!input.isHidden">
              <Field
                :id="'field-' + input.id"
                :class="{
                  'text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait': true,
                }"
                :key="input.name + '-' + input.id"
                :as="field.as"
                :name="'input_' + input.id"
                :placeholder="input.placeholder"
                :type="field.type"
                :disabled="submitting"
                :autocomplete="field.autocomplete"
                v-model="values['input_' + input.id]"
              />
              <label
                :for="'field-' + input.id"
                :class="[field.css, field.inputType, 'under-label']"
                >{{ input.label }}</label
              >
            </div>
          </div>
        </div>
        <div
          v-if="validationErrors[field.id.split('.')[0]]"
          class="error-message"
        >
          {{ validationErrors[field.id.split(".")[0]] }}
        </div>
      </div>

      <div v-else-if="field.type === 'hidden'">
        <Field
          :id="'field-' + field.id"
          :key="field.name + '-' + field.id"
          as="input"
          :name="field.name"
          :placeholder="field.placeholder"
          :type="field.type"
          :value="field.defaultValue"
          v-model="values[field.name]"
          :ref="field.name"
        />
      </div>

      <div v-else-if="field.type === 'checkbox'" class="checkboxes">
        <div
          class="checkbox"
          v-for="(choice, index) in field.choices"
          :key="index"
        >
          <input
            :id="'field-' + field.inputs[index].id"
            type="checkbox"
            :name="'input_' + field.inputs[index].id"
            :value="choice.value"
            :checked="choice.isSelected"
            @change="
              updateCheckboxValues(field.name, field.inputs[index].id, $event)
            "
          />
          <label :for="'field-' + field.inputs[index].id">{{
            choice.text
          }}</label>
        </div>
        <div
          class="content"
          v-html="field.content ? field.content : field.description"
        ></div>
      </div>

      <div v-else>
        <label
          :for="'field-' + field.id"
          :class="[field.css, field.inputType]"
          >{{ field.label }}</label
        >
        <Field
          v-if="
            field.type === 'email' ||
            field.type === 'text' ||
            field.type === 'password' ||
            field.type === 'date'
          "
          :id="'field-' + field.id"
          :class="{
            'text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait': true,
            textarea: field.type === 'textarea',
            [field.css]: true,
          }"
          :key="field.name + '-' + field.id"
          :as="field.as"
          :name="field.name"
          :placeholder="field.placeholder"
          :type="field.type"
          :rules="field.rules"
          :disabled="submitting"
          :autocomplete="field.autocomplete"
          :rows="field.type === 'textarea' ? 5 : null"
          v-model="values[field.name]"
          :ref="field.name"
        />

        <Field
          v-if="field.type === 'phone'"
          :id="'field-' + field.id"
          :class="{
            'text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait': true,
            textarea: field.type === 'textarea',
            [field.css]: true,
          }"
          :key="field.name + '-' + field.id"
          :as="field.as"
          :name="field.name"
          :placeholder="field.placeholder"
          :type="field.type"
          :rules="field.rules"
          :disabled="submitting"
          :autocomplete="field.autocomplete"
          :maxlength="14"
          v-model="field.phone"
        />

        <Field
          v-if="field.type === 'radio'"
          :id="'field-' + field.id"
          :class="{
            'text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait': true,
            textarea: field.type === 'textarea',
            [field.css]: true,
          }"
          :key="field.name + '-' + field.id"
          :as="field.as"
          :name="field.name"
          :placeholder="field.placeholder"
          :type="field.type"
          :rules="field.rules"
          :disabled="submitting"
          :autocomplete="field.autocomplete"
          :rows="field.type === 'textarea' ? 5 : null"
          v-model="values[field.name]"
        >
          <div
            class="radio-flex"
            v-for="(choice, index) in field.choices"
            :key="index"
          >
            <input
              :id="'choice-' + index"
              :value="choice.value"
              type="radio"
              :name="field.name"
              v-model="values[field.name]"
            />
            <label :for="'choice-' + index">{{ choice.text }}</label>
          </div>
        </Field>

        <div
          v-if="field.type === 'time'"
          :key="field.name + '-' + field.id"
          :class="'time-flex'"
        >
          <div v-for="(input, index) in field.inputs" :key="index">
            <input
              v-if="input.label === 'HH'"
              :id="'field-' + field.id + '-' + index"
              :name="field.name"
              :placeholder="input.label"
              type="number"
              min="1"
              max="12"
              v-model="field.time.hours"
            />
            <input
              v-else-if="input.label === 'MM'"
              :id="'field-' + field.id + '-' + index"
              :name="field.name"
              :placeholder="input.label"
              type="number"
              min="0"
              max="59"
              :value="formatMinutes(field)"
              @input="field.time.minutes = Number($event.target.value)"
            />
            <select
              v-else
              :id="'field-' + field.id + '-' + index"
              :name="field.name"
              v-model="field.time.period"
            >
              <option value="AM" selected>AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>

        <textarea
          v-if="field.type === 'textarea'"
          :id="'field-' + field.id"
          :class="{
            'text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait': true,
            textarea: field.type === 'textarea',
            [field.css]: true,
          }"
          :key="field.name + '-' + field.id"
          :as="field.as"
          :name="field.name"
          :placeholder="field.placeholder"
          :type="field.type"
          :rules="field.rules"
          :disabled="submitting"
          :autocomplete="field.autocomplete"
          :rows="field.type === 'textarea' ? 5 : null"
          @change="updateValues(field.name, $event.target.value)"
        />

        <!-- Select field -->
        <select
          v-else-if="field.type === 'select'"
          :id="'field-' + field.id"
          :class="`text-gray-500 w-full disabled:opacity-50 disabled:cursor-wait ${field.css}`"
          :name="field.name"
          :key="field.name + '-' + field.id"
          :rules="field.rules"
          :disabled="submitting"
          @change="updateValues(field.name, $event.target.value)"
        >
          <option
            v-for="choice in field.choices"
            :value="choice.value"
            :key="choice.value"
          >
            {{ choice.text }}
          </option>
        </select>
        <div v-if="validationErrors[field.simpleID]" class="error-message">
          {{ validationErrors[field.simpleID] }}
        </div>
        <div
          class="content"
          v-html="field.content ? field.content : field.description"
        ></div>
      </div>
    </component>

    <button
      type="submit"
      :class="[
        'button-primary w-auto text-center button button-1 button-medium w-fit mx-auto',
        submitting ? 'cursor-wait opacity-50' : 'cursor-pointer',
      ]"
    >
      {{ schema && schema.button ? schema.button.text : "Submit" }}
    </button>
  </Form>
  <div
    v-if="isSubmitted"
    v-html="displayMessage"
    id="confirmation-message"
  ></div>
</template>

<style>
#confirmation-message {
  @apply text-center px-8;
}

.form.dynamic-gravity-form {
  @apply px-8;

  span[role="alert"] {
    @apply block text-red-500 leading-none text-xs py-1;
  }
  #manual-error-message {
    @apply -mt-4;
  }

  .field-group-container {
  }

  .single-field-container {
    @apply relative;

    .checkbox {
      @apply flex items-center;
    }

    #vip-list {
      @apply text-sm mt-4;

      h2 {
        @apply text-base;
      }
    }

    input[type="name"],
    input[type="phone"] {
      @apply bg-white border-gray-500 border rounded-none pt-2 pr-3 pb-2 pl-3 text-base leading-6 shadow-none;
    }

    .under-label {
      font-size: 12px;
    }

    .flex-name {
      @apply flex justify-between gap-4 flex-wrap md:flex-nowrap;

      div {
        @apply w-full;
      }
    }

    ul {
      list-style-type: disc;
      list-style-position: inside;
    }

    .error-message {
      color: red;
      font-size: 12px;
    }

    .radio-flex {
      @apply flex items-center gap-2;
    }

    input[type="email"],
    input[type="select"] {
      @apply w-full !important;
    }
  }

  .time {
    input,
    select {
      @apply w-fit !important;
    }

    .time-flex {
      @apply flex gap-2;
    }
  }

  .button-primary {
    @apply flex items-center justify-center w-fit px-8 py-3 font-medium text-white border border-transparent shadow bg-primary-500 rounded-md hover:bg-primary-500/80 md:py-4 md:text-lg md:px-10;
  }

  .content {
    p {
      @apply leading-tight;
    }
  }
}
</style>

<script setup>
import { ref, onMounted, nextTick, reactive, watchEffect } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";

const props = defineProps({
  gfid: Number,
  submitting: Boolean,
  scroll: {
    type: Number,
    default: 0,
  },
});

let schema = ref(null);
let values = ref({});
let confirmationMessage = ref("");
let isSubmitted = ref(false);
let defaultConfirmationMessage =
  "Thanks for choosing Consort Homes, we will get back to you as soon as possible!";
let selectedOption = ref(null);
let validationErrors = reactive({});
let displayMessage = computed(() => {
  return confirmationMessage.value || defaultConfirmationMessage;
});

const updateValues = (key, value) => {
  values.value[key] = value || selectedOption.value;

  // If the updated field is a select field, update selectedOption
  const updatedField = schema.value.fields.find((field) => field.name === key);
  if (updatedField && updatedField.type === "select") {
    selectedOption.value = value;
  }
};

onMounted(async () => {
  try {
    const url = "/api/get-form-data/" + props.gfid;
    const response = await fetch(url);
    const responseBody = await response.text();

    if (!response.ok) {
      console.error("API request failed with status", response.status);
      return;
    }

    let gravityFormsData;
    try {
      gravityFormsData = JSON.parse(responseBody);
    } catch (error) {
      console.error("Failed to parse response body as JSON", error);
      return;
    }

    if (!gravityFormsData || !gravityFormsData.id || !gravityFormsData.fields) {
      console.error("Invalid data received", gravityFormsData);
      return;
    }

    schema.value = {
      id: gravityFormsData.id,
      button: {
        type: gravityFormsData.button.type,
        text: gravityFormsData.button.text,
        imageUrl: gravityFormsData.button.imageUrl,
        width: gravityFormsData.button.width,
      },
      fields: gravityFormsData.fields.map((field) => ({
        id: field.inputs ? field.inputs[0].id : field.id, // Use the id from the first input if it exists, otherwise use field.id
        simpleID: field.id,
        name: `input_${field.id}`,
        type: field.type,
        label: field.label,
        css: field.cssClass,
        placeholder: field.placeholder,
        inputType: field.inputType,
        content: field.content,
        description: field.description,
        inputs: field.inputs,
        choices: field.choices,
        defaultValue: field.defaultValue,
        defaultOption:
          Array.isArray(field.choices) && field.choices.length > 0
            ? field.choices[0].value
            : null,
        autocomplete: field.autocompleteAttribute, // Assuming autocompleteAttribute is used for autocomplete
      })),
    };
    // Set selectedOption to the defaultOption of the first select field
    const firstSelectField = schema.value.fields.find(
      (field) => field.type === "select",
    );
    if (firstSelectField) {
      selectedOption.value = firstSelectField.defaultOption;
      values.value[firstSelectField.name] = firstSelectField.defaultOption; // Set default value in values
    }

    schema.value.fields.forEach((field) => {
      if (field.type === "checkbox") {
        field.inputs.forEach((input, index) => {
          const inputName = "input_" + input.id.replace(/\./g, "_");

          if (field.choices[index].isSelected) {
            values.value[inputName] = field.choices[index].value;
          }
        });
      }
      if (field.type === "time") {
        // Create a reactive object for the time field
        const time = reactive({
          hours: null,
          minutes: null,
          period: "AM",
        });

        // Watch the time object and update the values object whenever it changes
        watch(
          time,
          (newTime) => {
            if (newTime.hours === null && newTime.minutes === null) {
              // If both hours and minutes are null, set the value to an empty string
              values.value[field.name] = "";
            } else {
              const paddedHours =
                newTime.hours !== null
                  ? String(newTime.hours).padStart(2, "0")
                  : "";
              const paddedMinutes =
                newTime.minutes !== null
                  ? String(newTime.minutes).padStart(2, "0")
                  : "";
              values.value[
                field.name
              ] = `${paddedHours}:${paddedMinutes} ${newTime.period}`;
            }
          },
          { deep: true },
        );

        // Add the time object to the field so it can be used in the template
        field.time = time;
      }
    });

    schema.value.fields.forEach((field) => {
      if (field.type === "phone") {
        // Create a computed property for the phone field
        field.phone = computed({
          get: () => values.value[field.name],
          set: (newValue) => {
            let value = newValue;
            value = value.replace(/\D/g, ""); // remove all non-digits
            value = value.substring(0, 10); // truncate to 10 digits
            const match = value.match(/^(\d{3})(\d{3})(\d{2,})$/);
            if (match) {
              values.value[
                field.name
              ] = `(${match[1]}) ${match[2]}-${match[3]}`;
            } else {
              values.value[field.name] = value;
            }
          },
        });
      }
    });
  } catch (error) {
    console.error("Error fetching form data", error);
  }
});

// Define a method to format the minutes
const formatMinutes = (field) => {
  return String(field.time.minutes).padStart(2, "0");
};

const onSubmit = async (values) => {
  // Push form_submission event to Google's dataLayer
  trackFormSubmission("gform_" + props.gfid);

  // Transform values object to use input names as keys
  const transformedValues = {};
  for (const key in values) {
    if (Array.isArray(values[key])) {
      // Handle compound inputs
      values[key].forEach((value, index) => {
        if (value !== undefined) {
          const newKey = `${key}.${index}`;
          transformedValues[newKey] = value;
        }
      });
    } else if (values[key] !== undefined) {
      // Handle regular inputs
      const newKey = `${key}`;
      transformedValues[newKey] = values[key];
    }
  }

  const username = "ck_03bfb99de67e2ab8ad44b45414e08bc203a51ac";
  const password = "cs_434e61a15204efcc4a43835b76b2a5c92c2eda5b";
  const base64Credentials = btoa(username + ":" + password);
  try {
    const response = await fetch(
      `https://consortadmin.kinsta.cloud/wp-json/gf/v2/forms/${props.gfid}/submissions`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${base64Credentials}`,
        },
        body: JSON.stringify(transformedValues),
      },
    );

    if (!response.ok) {
      //throw new Error(`HTTP error! status: ${response.status}`);
      const errorData = await response.json(); // Parse the server response
      throw new Error(JSON.stringify(errorData)); // Throw the server response
    }

    const data = await response.json();

    if (data.is_valid) {
      confirmationMessage.value = data.confirmation_message;
      isSubmitted.value = true;
      // Scroll to the thank you message
      nextTick(() => {
        const thankYouMessageElement = document.getElementById(
          "confirmation-message",
        );
        if (thankYouMessageElement) {
          const headerOffset = props.scroll;
          const elementPosition = thankYouMessageElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      });
    } else {
      // Handle validation errors
      validationErrors = reactive(data.validation_messages);
    }
  } catch (error) {
    console.error("Error submitting form:", error);
    if (error instanceof Error) {
      // If the error is due to a HTTP error, try to parse the validation errors
      try {
        const errorData = JSON.parse(error.message);
        validationErrors = errorData.validation_messages; // Update this line

        // Scroll to the top of the form
        const formElement = document.getElementById("gravity-form");
        if (formElement) {
          formElement.scrollIntoView({ behavior: "smooth" });
        }
      } catch (parseError) {
        console.error("Error parsing validation errors:", parseError);
      }
    }
  }
};

const updateCheckboxValues = (name, inputId, event) => {
  const inputName = "input_" + inputId.replace(/\./g, "_");

  if (!values.value[inputName]) {
    values.value[inputName] = [];
  }

  if (event.target.checked) {
    values.value[inputName] = event.target.value;
  } else {
    values.value[inputName] = "";
  }
};

const emit = defineEmits(["form-submit"]);
</script>
